import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Avatar,
} from "@mui/material";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Form-icon imports — same set the shared sidebar uses, so dashboard cards
// match the sidebar visually.
import BusinessIcon from "@mui/icons-material/Business";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import CloudIcon from "@mui/icons-material/Cloud";
import FactoryIcon from "@mui/icons-material/Factory";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BuildIcon from "@mui/icons-material/Build";

import AuditSidebar from "../components/AuditSidebar";
import { firestore } from "../components/firebase.config";
import { doc, onSnapshot } from "firebase/firestore";

// ─── Form metadata ──────────────────────────────────────────────────────────
//  Titles use "Step N:" prefix so users see the sequence clearly.
//  The `letter` field is kept for backward-compat with the sidebar / any
//  other component that references it.
const forms = [
  {
    key: "a",
    letter: "A",
    title: "Step 1: Manufacturing Facility",
    route: "form-a",
    status: "Not Started",
    progress: 0,
    updated: "Not updated yet",
    color: "#16a34a",
    bg: "#dcfce7",
    IconCmp: BusinessIcon,
  },
  {
    key: "b",
    letter: "B",
    title: "Step 2: Source Streams",
    route: "form-b",
    status: "Not Started",
    progress: 0,
    updated: "Not updated yet",
    color: "#ea580c",
    bg: "#ffedd5",
    IconCmp: LocalGasStationIcon,
  },
  {
    key: "c",
    letter: "C",
    title: "Step 3: Emissions & Energy",
    route: "form-c",
    status: "Not Started",
    progress: 0,
    updated: "Not updated yet",
    color: "#7c3aed",
    bg: "#ede9fe",
    IconCmp: CloudIcon,
  },
  {
    key: "d",
    letter: "D",
    title: "Step 4: Production Processes",
    route: "form-d",
    status: "Not Started",
    progress: 0,
    updated: "Not updated yet",
    color: "#2563eb",
    bg: "#dbeafe",
    IconCmp: FactoryIcon,
  },
  {
    key: "e",
    letter: "E",
    title: "Step 5: Precursors",
    route: "form-e",
    status: "Not Started",
    progress: 0,
    updated: "Not updated yet",
    color: "#d97706",
    bg: "#fef3c7",
    IconCmp: Inventory2Icon,
  },
  {
    key: "f",
    letter: "F",
    title: "Step 6: Tools & Calculations",
    route: "form-f",
    status: "Not Started",
    progress: 0,
    updated: "Not updated yet",
    color: "#0891b2",
    bg: "#cffafe",
    IconCmp: BuildIcon,
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

const getStatusChipProps = (status) => {
  if (status === "Completed") {
    return {
      label: "Completed",
      sx: { bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 700 },
    };
  }

  if (status === "In Progress") {
    return {
      label: "In Progress",
      sx: { bgcolor: "#ffedd5", color: "#ea580c", fontWeight: 700 },
    };
  }

  return {
    label: "Not Started",
    sx: { bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700 },
  };
};

const getButtonLabel = (status) => {
  if (status === "Completed") return "Open Form";
  if (status === "In Progress") return "Continue";
  return "Start";
};

const getStatusFromProgress = (progress) => {
  if (progress >= 100) return "Completed";
  if (progress >= 10) return "In Progress";
  return "Not Started";
};

const formatUpdatedTime = (timestamp) => {
  if (!timestamp) return "Not updated yet";

  const date = timestamp?.toDate
    ? timestamp.toDate()
    : new Date(timestamp);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// ─── Component ──────────────────────────────────────────────────────────────

const AuditDashboard = () => {
  const navigate = useNavigate();
  const { auditId } = useParams();

  const auth = getAuth();

  const displayName =
    auth.currentUser?.displayName ||
    auth.currentUser?.email?.split("@")[0] ||
    "User";

  const firstLetter = displayName.charAt(0).toUpperCase();

  const displayAuditId = auditId || "AUD-2026-001";
  const [formsData, setFormsData] = React.useState(forms);
  const [overallProgress, setOverallProgress] = React.useState(0);

  const lastUpdated = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  React.useEffect(() => {
    if (!auditId) return;

    const formKeys = ["a", "b", "c", "d", "e", "f"];

    const unsubscribers = formKeys.map((key) => {
      const ref = doc(
        firestore,
        "auditReports",
        auditId,
        `form_${key}`,
        "main"
      );

      return onSnapshot(ref, (snapshot) => {
        const formData = snapshot.exists() ? snapshot.data() : {};

        const progressMeta = formData?.progressMeta || {};

        const progress = progressMeta.progress || 0;
        const status = progressMeta.status || getStatusFromProgress(progress);
        const updated = formatUpdatedTime(formData?.updatedAt);
        const sections = progressMeta.sections || {};

        setFormsData((prevForms) => {
          const updatedForms = prevForms.map((form) =>
            form.key === key
              ? {
                  ...form,
                  progress,
                  status,
                  updated,
                  sections,
                }
              : form
          );

          const totalProgress = updatedForms.reduce(
            (sum, form) => sum + form.progress,
            0
          );

          setOverallProgress(
            Math.round(totalProgress / updatedForms.length)
          );

          return updatedForms;
        });
      });
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [auditId]);

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Shared sidebar — same component every form page uses.
          Has its own working collapse toggle + localStorage state, so the
          collapsed/expanded preference persists across the whole app. */}
      <AuditSidebar displayAuditId={displayAuditId} />

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* ── Top header bar ───────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "#fff",
            borderBottom: "1px solid #e2e8f0",
            px: 3,
            height: 64,
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography fontSize={10.5} color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4, textTransform: "uppercase" }}>
              Audit ID
            </Typography>
            <Typography fontWeight={700} fontSize={15}>
              {displayAuditId}
            </Typography>
          </Box>

          <Box>
            <Typography fontSize={10.5} color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4, textTransform: "uppercase" }}>
              Status
            </Typography>
            <Chip
              label={
                overallProgress >= 100
                  ? "Completed"
                  : overallProgress > 0
                  ? "In Progress"
                  : "Not Started"
              }
              size="small"
              sx={{
                bgcolor:
                  overallProgress >= 100
                    ? "#dcfce7"
                    : overallProgress > 0
                    ? "#eff6ff"
                    : "#f1f5f9",
                color:
                  overallProgress >= 100
                    ? "#16a34a"
                    : overallProgress > 0
                    ? "#2563eb"
                    : "#64748b",
                fontWeight: 600,
                mt: 0.3,
                fontSize: 11.5,
                height: 22,
                borderRadius: 99,
              }}
            />
          </Box>

          <Box>
            <Typography fontSize={10.5} color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4, textTransform: "uppercase" }}>
              Completion
            </Typography>
            <Typography fontWeight={700} fontSize={15} color="primary">
              {overallProgress}% Completed
            </Typography>
          </Box>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2.5 }}>
            <Box>
              <Typography fontSize={10.5} color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.4, textTransform: "uppercase" }}>
                Last Updated
              </Typography>
              <Typography fontWeight={600} fontSize={12.5}>
                {lastUpdated}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 1.25,
                py: 0.6,
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                bgcolor: "#fff",
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "#2563eb",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {firstLetter}
              </Avatar>

              <Typography fontSize={12.5} fontWeight={600}>
                {displayName}
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* ── Main content ───────────────────────────────── */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Box>
            <Card
              sx={{
                borderRadius: 2.5,
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
                mb: 2.5,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography fontWeight={700} fontSize={18} sx={{ mb: 2 }}>
                  Overall Audit Progress
                </Typography>

                <Stack direction="row" spacing={3} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={overallProgress}
                      sx={{
                        height: 8,
                        borderRadius: 99,
                        bgcolor: "#e2e8f0",
                        mb: 1.5,
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 99,
                          background: "linear-gradient(90deg, #1d4ed8, #2563eb)",
                        },
                      }}
                    />

                    <Typography fontSize={13} color="text.secondary">
                      You're making good progress. Keep completing the remaining steps.
                    </Typography>
                  </Box>

                  <Typography fontWeight={700} fontSize={36} color="primary">
                    {overallProgress}%
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography fontWeight={700} fontSize={18}>
                Audit Steps (1–6)
              </Typography>

              <Button
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderRadius: 1.5,
                  fontWeight: 600,
                  color: "#0f172a",
                  borderColor: "#e2e8f0",
                  fontSize: 12.5,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                View by: All
              </Button>
            </Stack>

            <Grid container spacing={2}>
              {formsData.map((form) => {
                const chipProps = getStatusChipProps(form.status);
                const IconCmp = form.IconCmp;

                return (
                  <Grid item xs={12} sm={6} lg={4} key={form.key}>
                    <Card
                      onClick={() => navigate(`/${form.route}/${auditId}`)}
                      sx={{
                        height: "100%",
                        borderRadius: 2.5,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
                        cursor: "pointer",
                        transition: "0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 24px rgba(15,23,42,0.10)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                          {/* Icon badge — same icon as the sidebar row */}
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              bgcolor: form.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <IconCmp sx={{ fontSize: 22, color: form.color }} />
                          </Box>

                          <Box>
                            <Typography fontWeight={700} fontSize={14.5} sx={{ mb: 0.5 }}>
                              {form.title}
                            </Typography>
                            <Chip
                              size="small"
                              {...chipProps}
                              sx={{ ...chipProps.sx, fontSize: 11, height: 20 }}
                            />
                          </Box>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={form.progress}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 99,
                              bgcolor: "#e2e8f0",
                              "& .MuiLinearProgress-bar": {
                                bgcolor: form.color,
                                borderRadius: 99,
                              },
                            }}
                          />
                          <Typography fontWeight={700} fontSize={12}>
                            {form.progress}%
                          </Typography>
                        </Stack>

                        <Typography color="text.secondary" fontSize={11.5} sx={{ mb: 1.5 }}>
                          Last updated: {form.updated}
                        </Typography>

                        <Button
                          fullWidth
                          variant="outlined"
                          endIcon={<ArrowForwardIosIcon sx={{ fontSize: "11px !important" }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${form.route}/${auditId}`);
                          }}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontWeight: 600,
                            color: "#2563eb",
                            borderColor: "#dbeafe",
                            py: 0.75,
                            height: 36,
                            fontSize: 13,
                            "&:hover": {
                              bgcolor: "#eff6ff",
                              borderColor: "#93c5fd",
                            },
                          }}
                        >
                          {getButtonLabel(form.status)}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuditDashboard;